// main.js
import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'lodash';
import PropTypes from 'prop-types';
import RCore from 'RCore';
import moment from 'moment-timezone';
import locales from '../../i18/moment/locale.all.js';

const ScreenSizeObserver = RCore.ScreenSizeObserver;
const Deeplinker = RCore.Deeplinker;

class WDPContext extends React.Component {
    static defaultProps = {
        client: 'core',
        theme: 'default',
        lang: 'en',
        user: 'rlz',
        tz: 'CET',
    };

    constructor(props) {
        super(props);
        const initState = props.initState || {};

        this.screenSizeObserver = new ScreenSizeObserver(props.parentWidth);

        /**
            In browser mode: WDPDeeplinker configured in window;
            In server mode: to render links serverside it should be configured and the only way to do this
            is do path deeplink configuration via GET params
        */

        if (typeof window === 'undefined' && this.props.deeplinker) {
            this.deeplinker = new Deeplinker(this.props.deeplinker);
        }

        if (typeof window !== 'undefined') {
            this.deeplinker = window.WDPDeeplinker && window.WDPDeeplinker;
        }

        this.state = Object.assign(
            {
                initState: !_.isEmpty(initState) && !initState.failed,
                load: { failed: false },
            },
            initState,
        );
    }

    getChildContext() {
        const ctx = WDPContext.provideBasicContext(this.props);

        ctx.deeplinker = this.deeplinker;
        ctx.screenSizeObserver = this.screenSizeObserver;
        ctx.plrLogoSettings = {
            display: this.props.plrImage,
        };
        ctx.teamLogoSettings = {
            display: this.props.teamLogo,
        };
        ctx.eventCellView = {
            display: this.props.eventCellView,
        };
        ctx.oddsProvider = {
            provider: this.props.odds, // provider name
        };
        ctx.trackPageView = this.trackPageView;
        ctx.trackEvent = this.trackEvent;

        const m = moment;

        _.toPairs(locales).forEach(pair => m.locale(pair[0], pair[1]));

        let lang = this.props.lang || 'en';

        if (lang === 'tc') {
            lang = 'zh-cn';
        }

        if (lang === 'sc') {
            lang = 'zh-hk';
        }

        m.locale(lang);
        m.tz.setDefault((this.props.tz && _.isString(this.props.tz) && this.props.tz) || 'CET');

        ctx.moment = m;
        // console.log("M", m, moment, m.locale(this.props.lang || 'en'))
        // ctx.moment.locale(this.props.lang || 'en')
        // ctx.moment = ;
        return ctx;
    }

    componentDidMount() {
        this.handleScreenSizeUpdate();
        this._debounceHandleScreenSizeUpdate = _.debounce(this.handleScreenSizeUpdate.bind(this), 500);

        window.addEventListener('resize', this._debounceHandleScreenSizeUpdate, false);
    }

    componentWillUnmount() {
        if (this._debounceHandleScreenSizeUpdate && this._debounceHandleScreenSizeUpdate.cancel) {
            this._debounceHandleScreenSizeUpdate.cancel();
        }

        window.removeEventListener('resize', this._debounceHandleScreenSizeUpdate, false);
    }

    static provideBasicContext(props) {
        const baseProps = ['theme', 'client', 'user', 'lang', 'name', 'deeplink', 'wiid', 'userContext'];

        const info = _.pick(props, baseProps);
        const widgetProps = _.omit(props, baseProps);

        info.social = props.social || _.get(this, 'props.children.props.social', false);

        if (info.lang && _.isArray(info.lang)) {
            info.lang = _.last(info.lang);
        }
        // parse user context
        info.userContext = info.userContext || '{}';

        if (typeof info.userContext === 'string') {
            try {
                info.userContext = JSON.parse(info.userContext || '{}');
            } catch (error) {
                console.error(error);
            }
        }

        return {
            info,
            widgetProps,
        };
    }

    /**
     *  This is half mocked context for test and server rendering.
     *  Deeplinker and ScreenSizeObserver are not important.
     *
     */
    static provideMockedContext(props) {
        const ctx = WDPContext.provideBasicContext(props);

        ctx.deeplinker = new Deeplinker();
        ctx.screenSizeObserver = new ScreenSizeObserver(320);

        const m = moment;

        m.tz.setDefault((props.tz && _.isString(props.tz) && props.tz) || 'CET');
        m.locale((props.lang && _.isString(props.lang) && props.lang) || 'en');

        ctx.moment = m;

        return ctx;
    }

    handleScreenSizeUpdate = () => {
        const width = ReactDOM.findDOMNode(this).offsetWidth;

        this.screenSizeObserver.onUpdateWidth(width);
    };

    static trackPageView() {
        const rlz_ga = window.rlz_ga;

        if (!rlz_ga || this.props.disableTrackPageView) {
            return;
        }

        const name = this.constructor.name;
        const { client, user, wiid } = this.context.info;
        const page =
            '/' +
            _.chain([client, user, name, wiid || '-'])
                .compact()
                .join('/')
                .value();
        const location = window.location.href;

        rlz_ga('main.set', 'campaignName', location);
        rlz_ga('main.set', 'screenName', location);
        // rlz_ga('main.set', 'pageview', page);
        rlz_ga('main.send', 'pageview', page);
        // rlz_ga('client.set', 'pageview', page);
        rlz_ga('client.send', 'pageview', page);
    }

    static childContextTypes = {
        info: PropTypes.object,
        widgetProps: PropTypes.object,
        deeplinker: PropTypes.object,
        screenSizeObserver: PropTypes.object,
        teamLogoSettings: PropTypes.object,
        eventCellView: PropTypes.object,
        plrLogoSettings: PropTypes.object,
        oddsProvider: PropTypes.object,
        moment: PropTypes.func,
        trackEvent: PropTypes.func,
        trackPageView: PropTypes.func,
    };

    static trackEvent(cat, act, lab) {
        const rlz_ga = window.rlz_ga;

        if (!rlz_ga) {
            return;
        }

        rlz_ga('main.send', 'event', cat, act, lab);
        rlz_ga('client.send', 'event', cat, act, lab);
    }

    render() {
        return <div>{this.props.children}</div>;
    }
}

export default WDPContext;



/** WEBPACK FOOTER **
 ** ../src/widgets/components/core/WDPContext.js
 **/